/**
 * Procedural green SHAPES — the ONE shared shape function (HANDOFF §3.2 / §3.5).
 *
 * Greens are no longer plain circles. Each green is a procedural outline (a
 * ROTATED ELLIPSE optionally deformed by a low-order radial LOBE) that FITS
 * INSIDE a consistent, slightly-bigger CONTAINER of bounding radius `green.radius`.
 *
 * The CONTAINER is the invariant: scoring rings (ace/eagle/birdie around the pin),
 * tee placement, hazard placement and the §5b calibration all key off `radius`
 * EXACTLY as before. Only the on-green SHAPE (par / bogey / render outline) changes.
 *
 * This module is PURE (no DOM, no globals, randomness only via the injected rng)
 * and is imported by BOTH `scoring.ts` (par/bogey) and `render/canvas.ts` (outline)
 * so WHAT YOU SEE IS WHAT SCORES — the par-test shape and the rendered shape are
 * the SAME function.
 *
 * Shape parameters (stored on {@link Green}, generated from the seeded rng):
 *  - `rx`, `ry`  semi-axes of the base ellipse (field-space px). The LARGER axis is
 *                ≤ `radius`; the smaller is `radius / e`. e=1 ⇒ round.
 *  - `rot`       ellipse rotation in radians [0, π). With a large `e` this yields
 *                long / wide / diagonal greens.
 *  - `lobe`      radial-lobe amplitude [0, ~0.22]: a `1 + lobe·cos(2·θ_local + phase)`
 *                harmonic that deforms the ellipse edge into oval/oblong/bean variety.
 *  - `lobePhase` phase of that harmonic [0, 2π).
 *
 * A green WITHOUT shape params (legacy literals / test fixtures) is treated as a
 * plain circle of `radius` — identical to the pre-shape behaviour.
 */
import type { Rng, ShapeRegion, Vec2 } from './types.ts';
import { FAIRWAY, SHAPE } from './constants.ts';

const TWO_PI = Math.PI * 2;

/** Resolved shape params with circle fallbacks, so legacy round greens still work. */
interface ShapeParams {
  readonly rx: number;
  readonly ry: number;
  readonly rot: number;
  readonly lobe: number;
  readonly lobePhase: number;
  /** Normaliser keeping the lobe-deformed edge inside the container (max R ≤ radius). */
  readonly lobeNorm: number;
}

/**
 * Resolve a green's shape params, falling back to a plain circle (rx=ry=radius,
 * rot=0, lobe=0) when the green carries none. `lobeNorm = 1 / (1 + lobe)` keeps the
 * lobe-deformed edge from poking past the larger semi-axis (worst case cos = +1).
 */
function shapeOf(green: ShapeRegion): ShapeParams {
  const rx = green.rx ?? green.radius;
  const ry = green.ry ?? green.radius;
  const rot = green.rot ?? 0;
  const lobe = green.lobe ?? 0;
  const lobePhase = green.lobePhase ?? 0;
  return { rx, ry, rot, lobe, lobePhase, lobeNorm: 1 / (1 + lobe) };
}

/**
 * Generate procedural shape parameters for a green of the given container radius
 * from the injected rng (HANDOFF §4.3). Produces a good MIX across greens:
 * round / oval / oblong / wide / long / diagonal (ellipse) plus organic / bean
 * variety (the radial lobe). The larger semi-axis is always ≤ `radius`, so the
 * shape fits the container; the smaller is `radius / e`.
 *
 * Draw order (fixed for determinism): elongation e → rotation rot → lobe → phase.
 */
export function makeGreenShape(
  radius: number,
  rng: Rng,
): { rx: number; ry: number; rot: number; lobe: number; lobePhase: number } {
  const e = SHAPE.elongationMin + rng() * (SHAPE.elongationMax - SHAPE.elongationMin);
  const rot = rng() * Math.PI; // [0, π): a half-turn covers every ellipse orientation
  const lobe = rng() * SHAPE.lobeMax;
  const lobePhase = rng() * TWO_PI;

  // The larger axis fills the container; the smaller is radius / e (e ≥ 1).
  // Randomly assign which of x/y is the long axis so rotation+elongation span
  // wide (long-x) and long (long-y) as well as the diagonals.
  const major = radius;
  const minor = radius / e;
  const longIsX = rng() < 0.5;
  const rx = longIsX ? major : minor;
  const ry = longIsX ? minor : major;

  return { rx, ry, rot, lobe, lobePhase };
}

/**
 * Boundary radius of the green outline in its LOCAL frame at local angle `theta`
 * (measured from +x in the un-rotated ellipse frame). This is the SINGLE source of
 * the outline: ellipse radius at θ, scaled by the normalised radial lobe.
 *
 * Ellipse polar radius: `r_e(θ) = rx·ry / hypot(ry·cosθ, rx·sinθ)`.
 * Lobe factor:          `(1 + lobe·cos(2θ + phase)) · lobeNorm`  (≤ 1, ≥ ~ (1−lobe)/(1+lobe)).
 */
function localRadiusAt(s: ShapeParams, theta: number): number {
  const ct = Math.cos(theta);
  const st = Math.sin(theta);
  const denom = Math.hypot(s.ry * ct, s.rx * st) || 1;
  const ellipse = (s.rx * s.ry) / denom;
  const lobeFactor = (1 + s.lobe * Math.cos(2 * theta + s.lobePhase)) * s.lobeNorm;
  return ellipse * lobeFactor;
}

/**
 * Does a field-space point rest INSIDE the green's procedural outline (§3.5 par)?
 *
 * Transforms `p` into the green's local frame (translate to centre, un-rotate by
 * `rot`), then compares its radius to {@link localRadiusAt} at its local angle.
 * Used by scoring (par) AND, scaled, by bogey — the same outline the renderer traces.
 */
export function pointInGreen(p: Vec2, green: ShapeRegion): boolean {
  return pointInScaledGreen(p, green, 1);
}

/**
 * Point-in-shape against the outline SCALED by `scale` about the green centre
 * (scale 1 = the green surface; scale (radius+bogeyMargin)/radius = the bogey
 * fringe). Monotonic in `scale`, so nesting (par ⊂ bogey) holds (§3.5).
 */
export function pointInScaledGreen(p: Vec2, green: ShapeRegion, scale: number): boolean {
  const s = shapeOf(green);
  const dx = p.x - green.center.x;
  const dy = p.y - green.center.y;
  // Un-rotate into the ellipse frame.
  const cr = Math.cos(-s.rot);
  const sr = Math.sin(-s.rot);
  const lx = dx * cr - dy * sr;
  const ly = dx * sr + dy * cr;
  const rLocal = Math.hypot(lx, ly);
  if (rLocal === 0) return true;
  const theta = Math.atan2(ly, lx);
  return rLocal <= localRadiusAt(s, theta) * scale;
}

/**
 * A point ON the green outline at world-frame angle bearing `theta` from centre —
 * for the renderer to trace the outline as a closed path. `scale` expands the
 * outline (e.g. the collar). Uses the SAME {@link localRadiusAt} as scoring, so the
 * rendered edge and the scored edge are identical.
 *
 * `theta` is a WORLD bearing; we rotate it into the local ellipse frame to read the
 * radius, then place the point back in world space at that world bearing — so the
 * sampled ring of `theta`s traces a closed loop without gaps.
 */
export function greenBoundaryPoint(green: ShapeRegion, theta: number, scale = 1): Vec2 {
  const s = shapeOf(green);
  const localTheta = theta - s.rot;
  const r = localRadiusAt(s, localTheta) * scale;
  return { x: green.center.x + Math.cos(theta) * r, y: green.center.y + Math.sin(theta) * r };
}

/**
 * Sample `segments` boundary points evenly around the outline (world bearings
 * 0..2π) into the provided `out` array of mutable points (allocation-sane: the
 * renderer hoists `out` and re-fills it per green per frame — §7). `scale` expands
 * the outline (collar). Returns `out`.
 */
export function sampleGreenOutline(
  green: ShapeRegion,
  segments: number,
  out: { x: number; y: number }[],
  scale = 1,
): { x: number; y: number }[] {
  const s = shapeOf(green);
  for (let i = 0; i < segments; i++) {
    const theta = (i / segments) * TWO_PI;
    const localTheta = theta - s.rot;
    const r = localRadiusAt(s, localTheta) * scale;
    const pt = out[i] ?? { x: 0, y: 0 };
    pt.x = green.center.x + Math.cos(theta) * r;
    pt.y = green.center.y + Math.sin(theta) * r;
    out[i] = pt;
  }
  return out;
}

/**
 * Generate the SHARED fairway shape params from the rng (§3.1) — the same kind of
 * rotated-ellipse-plus-lobe used for greens, but biased LONG so it reads as a
 * fairway STRIP. Returns dimensionless params only (no centre/size/orientation):
 *  - `e`         elongation (major / minor); higher = a narrower strip.
 *  - `lobe`      radial-lobe amplitude (organic edge), same harmonic as greens.
 *  - `lobePhase` phase of that harmonic.
 *  - `jitter`    a small random lean of the strip off the mid↔short diagonal; the
 *                tees re-centre onto the tilted axis, so their x shifts to match.
 *
 * ONE draw is shared by both sides (point-reflected) so the two fairways are
 * mirror-identical. Draw order is fixed (e → lobe → phase → jitter) for determinism.
 */
export function makeFairwayShape(rng: Rng): {
  e: number;
  lobe: number;
  lobePhase: number;
  jitter: number;
} {
  const e = FAIRWAY.elongationMin + rng() * (FAIRWAY.elongationMax - FAIRWAY.elongationMin);
  const lobe = rng() * FAIRWAY.lobeMax;
  const lobePhase = rng() * TWO_PI;
  const jitter = (rng() * 2 - 1) * FAIRWAY.axisJitter;
  return { e, lobe, lobePhase, jitter };
}
